import React from 'react';
import confirm from 'utils/confirm';
import noop from 'lodash/noop';
import moment from 'moment';
import UserBasicInfo from 'components/pure/admin/UserBasicInfo';
import createConnectedField from 'utils/createConnectedField';
import AdminEditUserActions from 'actions/admin/AdminEditUserActions';
import AdminEditUserStore, {
  ALL_VIEW_PERMISSIONS,
  VIEW_TEAM,
} from 'stores/admin/user/AdminEditUserStore';
import {List, Record, OrderedMap, Map} from 'immutable';
import {
  ADMIN_EDIT_USER_FIELD_ACTIVE,
  ADMIN_EDIT_USER_FIELD_CAN_SHARE_LIBRARIES_GLOBALLY,
  ADMIN_EDIT_USER_FIELD_CAN_SHARE_LIBRARIES_WITH_DEPARTMENTS,
  ADMIN_EDIT_USER_FIELD_CAN_SHARE_LIBRARIES_WITH_TEAMS,
  ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_GLOBALLY,
  ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_WITH_DEPARTMENTS,
  ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_WITH_TEAMS,
  ADMIN_EDIT_USER_FIELD_CAN_TWO_WAY_SYNC,
  ADMIN_EDIT_USER_FIELD_DEPARTMENT_STAT,
  ADMIN_EDIT_USER_FIELD_HIDDEN,
  ADMIN_EDIT_USER_FIELD_IS_COACH,
  ADMIN_EDIT_USER_FIELD_PERMISSION,
  ADMIN_EDIT_USER_FIELD_ROLE,
  ADMIN_EDIT_USER_FIELD_TEAM,
  ADMIN_EDIT_USER_FIELD_TEAMS_VISIBLE,
  ADMIN_EDIT_USER_FIELD_JOB,
  ADMIN_EDIT_USER_LANGUAGE_OPTION,
  ADMIN_EDIT_USER_FIELD_RECORD_SCHEDULE_CALLS,
} from 'utils/const/adminEditUserFields';
import EditUser, {
  DepartmentStatPermission,
  USER_CAN_VIEW_DEPT_STATS,
  USER_DASHBOARD_MODE_CAN_VIEW_DEPT_STATS,
  UserDashboardModeCanViewDeptStats,
  UserDashboardModeRoleLevelAdmin,
  UserDashboardModeRoleLevelManager,
  UserPermissionSet,
  ViewTeamPermission,
} from 'records/EditUser';
import Button from '@material-ui/core/Button';
import {browserHistory} from 'react-router';
import 'components/pure/admin/user/AdminEditUser.styl';
import 'styles/field-container/FieldContainer.styl';
import stripUrlProtocol from 'utils/url/stripUrlProtocol';
import scrollToElement from 'utils/scrollToElement';
import Breadcrumbs from 'components/pure/utils/Breadcrumbs';
import AdminUserAuditTrailSource from 'sources/admin/user/AdminUserAuditTrailSource';
import formatDateTimeHuman from 'utils/formatDateTimeHuman';
import FieldContainer from 'components/pure/utils/form/FieldContainer';
import FormError from 'components/pure/utils/form/FormError';
import FieldGroup from 'components/pure/utils/form/FieldGroup';
import InputField from 'components/pure/utils/form/InputField';
import ToggleField from 'components/pure/utils/form/ToggleField';
import {featureFlags} from 'utils/const';
import hasFlag from 'utils/hasFlag';
import {languageLabels, languageOptions} from 'utils/const/languageOptions';
import {IntegrationDataName, IntegrationType} from 'records/IntegrationDataInfo';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import {associateBy} from 'utils/associateBy';
import {PasswordSource} from '@store/password/PasswordSource';
import {errorToast} from '@store/toast/Toast.utils';
import GumUserInfoBox from 'components/pure/admin/profile/components/GumUserInfoBox';

const UserPermission = Record({
  permission: undefined,
});

/**
 * Render the Admin "User - Edit" page
 */
export default class AdminEditUser extends React.Component {
  // TODO Refactor this big component into smaller ones
  constructor(props) {
    super(props);
    this.state = {
      avatar: null,
      // readonly user audit trails fetched from the backend if this component
      // is rendered under the context of editing a user
      trails: null,
      passwordResetLoading: false,
      passwordResetSuccess: false,
    };
    this.submitForm = this.submitForm.bind(this);
    this.cancelSubmitForm = this.cancelSubmitForm.bind(this);

    // Initialize Admin Fields
    this.AdminUserToggleField = createConnectedField(ToggleField, AdminEditUserActions);

    this.AdminUserInputField = createConnectedField(InputField, AdminEditUserActions);
  }

  componentDidMount() {
    const fromDeptModal = this.props.location.state && this.props.location.state.fromDeptModal;
    if (fromDeptModal) {
      setTimeout(() => {
        scrollToElement(this.buttonRef);
      }, 1000);
    }

    // Retrieve the current target user's audit trails iff this page is rendered
    // under the context of rendering
    const targetUserId = this.props.params.userId;
    if (!!targetUserId) {
      setImmediate(() => {
        AdminUserAuditTrailSource.retrieve(targetUserId).then(trails => {
          this.setState({trails: trails});
        });
      });
    }
  }

  /**
   * Return an ordered Map that includes all the teams' ids + names
   */
  getTeamIdOptions() {
    const teams = this.props.accountFormData.teams || List();
    const options = teams
      .filter(team => team.active)
      .sortBy(team => team.name.toLowerCase())
      .map(team => ({
        label: team.name || '(Unnamed team)',
        value: team.id,
      }));
    return associateBy(options, o => o.value, OrderedMap());
  }

  /**
   * Render the multi-value dropdown box for the view-team permission that lists all the teams the editing user is
   * allowed to view
   * @param viewPermission {UserPermission} the editing user's view-related permission
   */
  renderTeamsVisibleField(viewPermission) {
    const teamMap = this.getTeamIdOptions();

    const name = ADMIN_EDIT_USER_FIELD_TEAMS_VISIBLE;
    const fieldValues = this.props.editState.formState.fieldValues;
    const userInfo = fieldValues.userInfo;
    const permissions = fieldValues.permissions.get(name);

    const value = permissions.map(p => teamMap.get(p.teamId)).filter(x => x);

    if (userInfo.role !== 'admin' && viewPermission.permission === VIEW_TEAM) {
      return (
        <FieldContainer label={'Teams Visible'} error={this.getError(name)}>
          <Autocomplete
            multiple
            autoHighlight
            disableClearable
            className="user-form-select"
            options={teamMap.toArray()}
            renderInput={params => <TextField {...params} size="small" variant="outlined" />}
            getOptionLabel={opt => opt.label}
            getOptionSelected={(opt, selected) => opt.value === selected.value}
            ChipProps={{
              size: 'small',
              variant: 'outlined',
            }}
            // connected field props
            onChange={(e, values) => {
              const viewTeamPermissions = values.map(
                opt => new ViewTeamPermission({teamId: opt.value})
              );
              AdminEditUserActions.changeField(name, viewTeamPermissions);
            }}
            onBlur={() => AdminEditUserActions.blurField(name)}
            value={value}
          />
        </FieldContainer>
      );
    } else {
      // When the user is not selecting teams for a permission, we do not render
      // the team selection field
      return <div />;
    }
  }

  /**
   * Render the section related to a user's library permission based on the user's whole permission set
   * @param permissionSet {UserPermissionSet}
   */
  renderLibraryPermissionsField(permissionSet) {
    return (
      <div className={'permission-container'}>
        <div className={'title'}>Library Permissions</div>

        <div className={'options-container'}>
          <FieldContainer
            className={'toggle'}
            error={this.getError(ADMIN_EDIT_USER_FIELD_CAN_SHARE_LIBRARIES_GLOBALLY)}>
            <this.AdminUserToggleField
              formState={permissionSet}
              name={ADMIN_EDIT_USER_FIELD_CAN_SHARE_LIBRARIES_GLOBALLY}
              label={'Can share libraries globally'}
              labelPosition={'right'}
            />
          </FieldContainer>

          <FieldContainer
            className={'toggle'}
            error={this.getError(ADMIN_EDIT_USER_FIELD_CAN_SHARE_LIBRARIES_WITH_DEPARTMENTS)}>
            <this.AdminUserToggleField
              formState={permissionSet}
              name={ADMIN_EDIT_USER_FIELD_CAN_SHARE_LIBRARIES_WITH_DEPARTMENTS}
              label={'Can share libraries with departments'}
              labelPosition={'right'}
            />
          </FieldContainer>

          <FieldContainer
            className={'toggle'}
            error={this.getError(ADMIN_EDIT_USER_FIELD_CAN_SHARE_LIBRARIES_WITH_TEAMS)}>
            <this.AdminUserToggleField
              formState={permissionSet}
              name={ADMIN_EDIT_USER_FIELD_CAN_SHARE_LIBRARIES_WITH_TEAMS}
              label={'Can share libraries with teams'}
              labelPosition={'right'}
            />
          </FieldContainer>
        </div>
      </div>
    );
  }

  /**
   * Render the section for trigger-permissions
   * @param permissionSet {UserPermissionSet}
   */
  renderTriggerPermissionsField(permissionSet) {
    return (
      <div className={'permission-container'}>
        <div className={'title'}>Smart Alert Permissions</div>
        <div className={'options-container'}>
          <FieldContainer
            className={'toggle'}
            error={this.getError(ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_GLOBALLY)}>
            <this.AdminUserToggleField
              formState={permissionSet}
              name={ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_GLOBALLY}
              label={'Can share smart alerts globally'}
              labelPosition={'right'}
            />
          </FieldContainer>

          <FieldContainer
            className={'toggle'}
            error={this.getError(ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_WITH_DEPARTMENTS)}>
            <this.AdminUserToggleField
              formState={permissionSet}
              name={ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_WITH_DEPARTMENTS}
              label={'Can share smart alerts with departments'}
              labelPosition={'right'}
            />
          </FieldContainer>

          <FieldContainer
            className={'toggle'}
            error={this.getError(ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_WITH_TEAMS)}>
            <this.AdminUserToggleField
              formState={permissionSet}
              name={ADMIN_EDIT_USER_FIELD_CAN_SHARE_TRIGGERS_WITH_TEAMS}
              label={'Can share smart alerts with teams'}
              labelPosition={'right'}
            />
          </FieldContainer>
        </div>
      </div>
    );
  }

  /**
   * Render the Login/Visibility toggle fields
   * @param userInfo {UserInfo}
   */
  renderLoginVisibilityField(userInfo) {
    return (
      <div className={'permission-container'}>
        <div className={'title'}>Login/Visibility</div>

        <div className={'options-container'}>
          <FieldContainer className={'toggle'} error={this.getError(ADMIN_EDIT_USER_FIELD_ACTIVE)}>
            <this.AdminUserToggleField
              formState={userInfo}
              name={ADMIN_EDIT_USER_FIELD_ACTIVE}
              label={'Login enabled'}
              labelPosition={'right'}
              toggled={this.userActive()}
            />
          </FieldContainer>

          <FieldContainer className={'toggle'} error={this.getError(ADMIN_EDIT_USER_FIELD_HIDDEN)}>
            <this.AdminUserToggleField
              formState={userInfo}
              name={ADMIN_EDIT_USER_FIELD_HIDDEN}
              label={'Archived (prevents login and hides in UI'}
              labelPosition={'right'}
            />
          </FieldContainer>
        </div>
      </div>
    );
  }

  /**
   * Render the Save/Cancel buttons
   */
  renderSaveCancel() {
    const editState = this.props.editState;
    return (
      <div ref={r => (this.buttonRef = r)} className={'button-container'}>
        <Button
          variant="contained"
          color="primary"
          className="ev-button"
          disabled={editState.isSubmitLoading}
          onClick={this.submitForm}>
          Save
        </Button>
        <Button
          variant="outlined"
          className="ev-button"
          disabled={editState.isSubmitLoading}
          onClick={this.cancelSubmitForm}>
          Cancel
        </Button>
      </div>
    );
  }

  /**
   * Submit the form to save/edit a user
   */
  submitForm() {
    const userId = this.props.params.userId;
    const fieldValues = this.cleanupFieldValues(this.props.editState.formState.fieldValues);
    const errors = AdminEditUserStore.validateFieldValues(fieldValues, true);

    const permissionSet = this.getUserPermissionSet(
      this.props.editState.formState.fieldValues.permissions.permissions
    );

    const dashboardMode = this.getUserDashboardMode(permissionSet);

    // if they didn't choose Select Departments but still have department level permissions,
    // we have to clear them out.
    let permissions =
      dashboardMode === UserDashboardModeCanViewDeptStats
        ? fieldValues.permissions
        : fieldValues.permissions
            .set(
              'permissions',
              fieldValues.permissions.permissions.filter(i => i !== USER_CAN_VIEW_DEPT_STATS)
            )
            .set('departmentStatPermissions', []);

    permissions = permissions.permissions.includes(VIEW_TEAM)
      ? permissions
      : permissions.set('viewTeamPermissions', []);

    const withUpdatedPermissionPermissions = fieldValues.set('permissions', permissions);
    const slackEmail = withUpdatedPermissionPermissions.userInfo.slackEmail;
    const withCleanSlackEmail = withUpdatedPermissionPermissions.set(
      'userInfo',
      withUpdatedPermissionPermissions.userInfo.set(
        'slackEmail',
        slackEmail === '' ? undefined : slackEmail
      )
    );

    AdminEditUserActions.validateAndSubmitForm(
      userId,
      this.state.avatar,
      withCleanSlackEmail,
      errors,
      !!this.props.params.userId
    );
  }

  /*
   * Clean up the values inside fieldValues before submitting it
   * @param fieldValues {EditUser}
   */
  cleanupFieldValues(fieldValues) {
    const integrations = fieldValues.integrations.filter(
      x => x.externId !== null && x.externId !== undefined
    );
    let userInfo = fieldValues.userInfo;
    if (
      userInfo.userId === null ||
      userInfo.userId === undefined ||
      userInfo.accountId === null ||
      userInfo.accountId === undefined
    ) {
      const loggedInUser = this.props.loggedInUser;
      // If this is a request to create a new user, then we fill `userInfo.userId` with a dummy value because the
      // backend requires userId to be not-null
      userInfo = userInfo.set('userId', 'dummyUserId').set('accountId', loggedInUser.accountId);
    }

    return new EditUser({
      userInfo: userInfo,
      permissions: fieldValues.permissions,
      integrations: integrations,
    });
  }

  /**
   * Cancel the current form's operations and exit to the parent page
   */
  cancelSubmitForm() {
    const doCancel = () => browserHistory.goBack();
    if (this.props.editState.formState.dirty) {
      const message = "Are you sure? If you click yes, you'll lose all of your edits.";
      confirm(message).then(doCancel, noop);
    } else {
      doCancel();
    }
  }

  /**
   * Render the list of integrations fields
   */
  renderIntegrationsFieldGroup() {
    const integrationsSeq = this.props.userIntegrationsMap.valueSeq();

    const hasCrmIntegrations = Boolean(
      integrationsSeq.find(integration => integration.type === IntegrationType.CRM)
    );

    const integrations = integrationsSeq
      .filter(integration => {
        // Show S3ScraperV3 caller ID field only if account has other CRM integrations
        if (integration.name === IntegrationDataName.S3SCRAPERV3) {
          return hasCrmIntegrations;
        }
        return true;
      })
      .toArray();

    return integrations.length > 0 ? (
      <FieldGroup title={'Integrations'}>
        {integrations.map(integration => this.renderIntegrationField(integration))}
      </FieldGroup>
    ) : (
      <></>
    );
  }

  renderRecordingFieldGroup(formState) {
    return (
      <FieldGroup title={'Recording'}>
        <FieldContainer
          className={'toggle'}
          error={this.getError(ADMIN_EDIT_USER_FIELD_RECORD_SCHEDULE_CALLS)}>
          <this.AdminUserToggleField
            formState={formState}
            name={ADMIN_EDIT_USER_FIELD_RECORD_SCHEDULE_CALLS}
            label={'Record scheduled calls'}
            labelPosition={'right'}
            formStateValueMapper={val => !val}
            formValueChangeMapper={val => !val}
          />
        </FieldContainer>
        <div className={'description'}>
          Internal meetings {hasFlag(featureFlags.doNotRecordInternalMeetings) ? 'won’t' : 'will'}{' '}
          be recorded. To change this, please talk to your administrator.
        </div>
      </FieldGroup>
    );
  }

  /**
   * Render the dropdown to select a team for the user
   */
  renderTeamPicker() {
    const name = ADMIN_EDIT_USER_FIELD_TEAM;
    const userInfo = this.props.editState.formState.fieldValues.userInfo;
    const selectedTeam = userInfo.get(ADMIN_EDIT_USER_FIELD_TEAM);
    const teamMap = this.getTeamIdOptions();

    return (
      <FieldGroup title="Team">
        <FieldContainer label="Team Name" error={this.getError(name)} errorOnBlur={false}>
          <Autocomplete
            autoHighlight
            disableClearable
            className="user-form-select"
            options={teamMap.toArray()}
            renderInput={params => <TextField {...params} size="small" variant="outlined" />}
            getOptionLabel={opt => opt.label ?? ''}
            getOptionSelected={(opt, selected) => opt.value === selected.value}
            ChipProps={{
              size: 'small',
              variant: 'outlined',
            }}
            // connected field props
            onChange={(e, opt) => AdminEditUserActions.changeField(name, opt.value)}
            onBlur={() => AdminEditUserActions.blurField(name)}
            value={teamMap.get(selectedTeam) ?? {}}
          />
        </FieldContainer>
      </FieldGroup>
    );
  }

  handlePasswordReset = async () => {
    const userInfo = this.props.editState.formState.fieldValues.userInfo;
    this.setState({passwordResetLoading: true});
    try {
      await PasswordSource.resetPasswordForUser(userInfo.userId);
      this.setState({passwordResetSuccess: true});
    } catch {
      errorToast('Failed to reset password');
    } finally {
      this.setState({passwordResetLoading: false});
    }
  };

  /**
   * Render a password reset form component to allow the admin to trigger a password reset for this user
   * @param userIsActive {Boolean}
   * @param userAccountHasIdentityProvider {Boolean}
   */
  renderPasswordReset(userIsActive, userAccountHasIdentityProvider) {
    const userInfo = this.props.editState.formState.fieldValues.userInfo;
    if (userIsActive && !userAccountHasIdentityProvider) {
      return (
        <FieldGroup title={'Password'}>
          <FieldContainer>
            {this.state.passwordResetLoading ? (
              <Button variant="contained" color="primary" className="ev-button" disabled={true}>
                Sending
              </Button>
            ) : this.state.passwordResetSuccess ? (
              <div>
                Success! We sent an email to {userInfo.firstName} with further instructions.
              </div>
            ) : (
              <Button variant="outlined" fullWidth onClick={this.handlePasswordReset}>
                Reset Password
              </Button>
            )}
          </FieldContainer>
        </FieldGroup>
      );
    }
  }

  renderUserAuditTrails() {
    const trails = this.state.trails;
    // Not all users have audit trails available to render.
    // If an action cannot be found among `trails`, then `find` returns `undefined`
    const lastCreated = trails && trails.find(({action}) => action === 'create');
    const lastUpdated = trails && trails.find(({action}) => action === 'update');
    const lastCreatedEmail = lastCreated?.actingUser?.email || null;
    return (
      <FieldGroup title={`Audit Trail`} className={`audit-trail-table`}>
        <table>
          <thead>
            <tr>
              <th />
              <th>By</th>
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            {lastCreated && (
              <tr>
                <td>User added</td>
                <td>{lastCreatedEmail}</td>
                <td>{formatDateTimeHuman(moment(lastCreated.created))}</td>
              </tr>
            )}
            {lastUpdated && (
              <tr>
                <td>Last updated</td>
                <td>{lastUpdated.actingUser.email}</td>
                <td>{formatDateTimeHuman(moment(lastUpdated.created))}</td>
              </tr>
            )}
          </tbody>
        </table>
      </FieldGroup>
    );
  }

  /**
   * Render the field to pick a dashboard view mode and related departmentIds for the user
   * @param deptStatPermissions {Array.DepartmentStatPermission}
   * @param permissionSet {UserPermissionSet}
   */
  renderDashboardMode(deptStatPermissions, permissionSet, glow) {
    const dashboardMode = this.getUserDashboardMode(permissionSet);
    const options = this.getDashboardModeOptions();
    return (
      <FieldGroup className={`department-container ${glow && 'glow'}`} title="Dashboards">
        <FieldContainer label="Dashboard Mode">
          <Select
            className="user-form-select"
            MenuProps={{
              getContentAnchorEl: null,
              anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
            }}
            value={dashboardMode.value}
            onChange={e => AdminEditUserActions.changeDashboardMode(e.target.value)}>
            {options.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FieldContainer>
        {this.renderDepartmentDashboardModeDropdown(dashboardMode)}
      </FieldGroup>
    );
  }

  /**
   * Render the dropdown for picking a list of departments for the user's can-view-department-stats mode
   * @param dashboardMode {UserDashboardMode}
   */
  renderDepartmentDashboardModeDropdown(dashboardMode) {
    if (dashboardMode.value === USER_DASHBOARD_MODE_CAN_VIEW_DEPT_STATS) {
      const name = ADMIN_EDIT_USER_FIELD_DEPARTMENT_STAT;

      const departments = this.props.accountFormData.departments || List();
      const activeDepartmentIds = departments
        .filter(d => d.active)
        .sortBy(d => d.name.toLowerCase())
        .map(d => d.id)
        .toArray();
      const departmentLabels = Map(departments.map(d => [d.id, d.name]));

      const permissions =
        this.props.editState.formState.fieldValues.permissions.departmentStatPermissions;
      const value = permissions.map(p => p.departmentId);

      return (
        <FieldContainer label={'Department Stats Visible'} error={this.getError(name)}>
          <Autocomplete
            multiple
            autoHighlight
            fullWidth
            filterSelectedOptions
            className="user-form-select"
            options={activeDepartmentIds}
            renderInput={params => <TextField {...params} size="small" variant="outlined" />}
            getOptionLabel={id => departmentLabels.get(id)}
            ChipProps={{
              size: 'small',
              variant: 'outlined',
            }}
            // connected field props
            name={name}
            onChange={(e, ids) => {
              const departmentPermissions = ids.map(
                id => new DepartmentStatPermission({departmentId: id})
              );
              AdminEditUserActions.changeField(name, departmentPermissions);
            }}
            onBlur={() => AdminEditUserActions.blurField(name)}
            value={value}
          />
        </FieldContainer>
      );
    }
  }

  /**
   * Return the options for the dashboard mode dropdown
   * @returns {*[UserDashboardMode]}
   */
  getDashboardModeOptions() {
    return [this.getRoleLevelOption(), UserDashboardModeCanViewDeptStats];
  }

  getRoleLevelOption() {
    const role = this.props.editState.getIn(['formState', 'fieldValues', 'userInfo', 'role']);
    return role === 'admin' ? UserDashboardModeRoleLevelAdmin : UserDashboardModeRoleLevelManager;
  }

  /**
   * Return the dashboard mode of the user from editUser.permissions
   * @param permissionSet {UserPermissionSet}
   * @return dashboardMode {UserDashboardMode}
   */
  getUserDashboardMode(permissionSet) {
    if (permissionSet.CAN_VIEW_DEPT_STATS !== undefined) {
      // user's dashboard mode is can-view-departments' stats, which means `deptStatPermissions` is going to be a
      // not-empty list
      return UserDashboardModeCanViewDeptStats;
    } else {
      // user's dashboard mode is can-view-role-level stats, which means the `deptStatPermissions` is going to be an
      // empty list
      return this.getRoleLevelOption();
    }
  }

  /**
   * Render the field for an integration configured in the admin's account
   * @param userIntegration {UserIntegrationExtern}
   */
  renderIntegrationField(userIntegration) {
    let label = userIntegration.label;
    if (userIntegration.host !== null && userIntegration.host !== undefined) {
      // Remove http:// or https:// from userIntegration.host
      const host = stripUrlProtocol(userIntegration.host);
      label = `${userIntegration.name} ${host}`;
    }
    return (
      <FieldContainer
        key={userIntegration.id}
        labelClassName={'integration-field-label-text'}
        label={label}
        error={this.getError(userIntegration.id)}>
        <this.AdminUserInputField
          formState={this.props.userIntegrationsMap}
          name={userIntegration.id}
          placeholder={`${userIntegration.name} Id`}
          type={'text'}
          formStateValueMapper={userIntegration => userIntegration.externId}
        />
      </FieldContainer>
    );
  }

  /**
   * Render the Language Spoken select field
   */
  renderLanguageOptions() {
    const userInfo = this.props.editState.formState.fieldValues.userInfo;
    const name = ADMIN_EDIT_USER_LANGUAGE_OPTION;
    const userLanguages = (userInfo.get(name) ?? []).filter(lang => languageOptions.includes(lang));
    return (
      <FieldGroup title="Languages Spoken">
        <FieldContainer label="Languages" error={this.getError(name)}>
          <Autocomplete
            multiple
            autoHighlight
            disableClearable
            fullWidth
            className="user-form-select"
            options={languageOptions}
            renderInput={params => <TextField {...params} size="small" variant="outlined" />}
            getOptionLabel={o => languageLabels[o]}
            getOptionSelected={(opt, value) => opt.value === value}
            ChipProps={{
              size: 'small',
              variant: 'outlined',
            }}
            // connected field props
            onChange={(e, values) => AdminEditUserActions.changeField(name, values)}
            onBlur={() => AdminEditUserActions.blurField(name)}
            value={userLanguages}
          />
        </FieldContainer>
      </FieldGroup>
    );
  }

  /**
   * Render the actual form for an admin/manager to create/edit a user
   * @returns {JSX.Element}
   */
  renderForm() {
    const editState = this.props.editState;
    const fromDeptModal = this.props.location.state && this.props.location.state.fromDeptModal;
    const {avatar} = this.state;
    const viewPermission = this.getViewPermission(editState.formState.fieldValues);
    // A list of strings, each of which represent a permission chosen for a user
    const permissionSet = this.getUserPermissionSet(
      editState.formState.fieldValues.permissions.permissions
    );
    const deptStatPermissions =
      editState.formState.fieldValues.permissions.departmentStatPermissions;
    const userInfo = editState.formState.fieldValues.userInfo;
    const userAccountHasIdentityProvider = this.props.loggedInUser.accountHasIdentityProvider;
    const userIsActive = userInfo.active;
    const jobMap = associateBy(this.props.jobOptions, opt => opt.value, OrderedMap());
    const isGUMUser = !!this.props.loggedInUser.tenantId;

    return (
      <div className="user-form-container">
        <FormError show={editState.formState.get('generalError')}>
          {editState.formState.get('generalError')}
        </FormError>
        <div className={'form-body ev-form'}>
          <div className="left">
            {isGUMUser ? (
              <div className="gum-profile-spacer">
                <GumUserInfoBox
                  user={editState.formState.fieldValues.userInfo}
                  userZoneFunction={tz => AdminEditUserActions.changeField('timezone', tz)}
                  isAdminEdit={true}
                />
              </div>
            ) : (
              <FieldGroup title="Main details">
                <UserBasicInfo
                  formState={editState.formState}
                  formData={this.props.accountFormData}
                  enableEmailChange={true}
                  avatar={avatar}
                  setAvatar={avatar => this.setState({avatar})}
                  userId={this.props.params.userId}
                  hasSlack={this.props.hasSlack}
                  hasSlackLoaded={this.props.hasSlackLoaded}
                />
              </FieldGroup>
            )}
            <FieldGroup title="Role & Permissions">
              <FieldContainer label="Role" error={this.getError(ADMIN_EDIT_USER_FIELD_ROLE)}>
                <Select
                  name={ADMIN_EDIT_USER_FIELD_ROLE}
                  className="user-form-select"
                  MenuProps={{
                    getContentAnchorEl: null,
                    anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
                  }}
                  value={userInfo.role}
                  onChange={e =>
                    AdminEditUserActions.changeField(ADMIN_EDIT_USER_FIELD_ROLE, e.target.value)
                  }>
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FieldContainer>

              <FieldContainer
                label="Call Visibility"
                error={this.getError(ADMIN_EDIT_USER_FIELD_PERMISSION)}>
                <Select
                  name={ADMIN_EDIT_USER_FIELD_PERMISSION}
                  className="user-form-select"
                  MenuProps={{
                    getContentAnchorEl: null,
                    anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
                  }}
                  value={viewPermission.get(ADMIN_EDIT_USER_FIELD_PERMISSION) ?? ''}
                  onChange={e =>
                    AdminEditUserActions.changeField(
                      ADMIN_EDIT_USER_FIELD_PERMISSION,
                      e.target.value
                    )
                  }>
                  <MenuItem value="VIEW_SELF">Can view own calls</MenuItem>
                  {userInfo.role !== 'admin' && (
                    <MenuItem value="VIEW_TEAM">Can view team calls</MenuItem>
                  )}
                  <MenuItem value="VIEW_ALL_DEPARTMENT">Can view department calls</MenuItem>
                  <MenuItem value="VIEW_ALL_ACCOUNT">Can view all calls</MenuItem>
                </Select>
              </FieldContainer>

              {hasFlag(featureFlags.adminKpis) && (
                <FieldContainer label="Job" error={this.getError(ADMIN_EDIT_USER_FIELD_JOB)}>
                  <Autocomplete
                    autoHighlight
                    disableClearable
                    className="user-form-select"
                    options={jobMap.toArray()}
                    renderInput={params => (
                      <TextField {...params} size="small" variant="outlined" />
                    )}
                    getOptionLabel={opt => opt.label ?? ''}
                    getOptionSelected={(opt, selected) => opt.value === selected.value}
                    ChipProps={{
                      size: 'small',
                      variant: 'outlined',
                    }}
                    // connected field props
                    onChange={(e, opt) => {
                      AdminEditUserActions.changeField(ADMIN_EDIT_USER_FIELD_JOB, opt.value);
                    }}
                    onBlur={() => AdminEditUserActions.blurField(ADMIN_EDIT_USER_FIELD_JOB)}
                    value={jobMap.get(userInfo.jobId) ?? {}}
                  />
                </FieldContainer>
              )}

              {this.renderTeamsVisibleField(viewPermission)}

              <div className={'permission-container'}>
                <div className={'title'}>Coach</div>
                <div className={'options-container'}>
                  <FieldContainer
                    className={'toggle'}
                    error={this.getError(ADMIN_EDIT_USER_FIELD_IS_COACH)}>
                    <this.AdminUserToggleField
                      formState={editState.formState.fieldValues.userInfo}
                      name={ADMIN_EDIT_USER_FIELD_IS_COACH}
                      label={'User is coach'}
                      labelPosition={'right'}
                    />
                  </FieldContainer>
                  <div className={'description'}>
                    Users who are coaches can create coaching plans, and get their own coach
                    dashboards (if they are not already managers of a team).
                  </div>
                </div>
              </div>

              {this.renderLibraryPermissionsField(permissionSet)}
              {this.renderTriggerPermissionsField(permissionSet)}

              <div className={'permission-container'}>
                <div className={'title'}>Other</div>

                <div className={'options-container'}>
                  <FieldContainer
                    className={'toggle'}
                    error={this.getError(ADMIN_EDIT_USER_FIELD_CAN_TWO_WAY_SYNC)}>
                    <this.AdminUserToggleField
                      formState={permissionSet}
                      name={ADMIN_EDIT_USER_FIELD_CAN_TWO_WAY_SYNC}
                      label={'Can perform two way sync on any calls'}
                      labelPosition={'right'}
                    />
                  </FieldContainer>
                </div>
              </div>

              {!isGUMUser && this.renderLoginVisibilityField(userInfo)}
            </FieldGroup>
            {/* Render Save/Cancel buttons here */}
            {this.renderSaveCancel()}
          </div>

          <div className={'right'}>
            {this.renderRecordingFieldGroup(userInfo)}
            {this.renderIntegrationsFieldGroup()}
            {this.renderTeamPicker()}
            {this.renderPasswordReset(userIsActive, userAccountHasIdentityProvider)}
            {userInfo.role !== 'user' &&
              this.renderDashboardMode(deptStatPermissions, permissionSet, fromDeptModal)}
            {this.renderUserAuditTrails()}
            {this.renderLanguageOptions()}
          </div>
        </div>
      </div>
    );
  }

  render() {
    // userId comes from Router.js:
    // <Route path="edit(/:userId)" component={AdminEditUserContainer} />
    const isEdit = !!this.props.params.userId;
    // breadcrumbs-style navigation bar at the top of the page
    const breadcrumbLinks = [
      {
        name: 'Users',
        url: '/admin/users',
      },
    ];
    const shouldRenderForm = !this.props.editState.isInitialLoad;
    return (
      <div className="user-form-container">
        <div className="wrapper">
          <Breadcrumbs links={breadcrumbLinks} />
          <h1>{'User - ' + (isEdit ? 'Edit' : 'Create')}</h1>
          {shouldRenderForm ? this.renderForm() : null}
        </div>
      </div>
    );
  }

  /**
   * Return the view-related permission out of all permissions specified in `editUser`
   * @param editUser {EditUser}
   */
  getViewPermission(editUser) {
    const viewPermission = editUser.permissions.permissions.find(perm =>
      ALL_VIEW_PERMISSIONS.includes(perm)
    );
    return UserPermission({permission: viewPermission});
  }

  /**
   * Return a user permission set that encapsulates all the canX permissions of a user
   * See javaapis/base/src/main/kotlin/io/execvision/model/UserPermission.kt
   * @param permissions {Array.String}
   */
  getUserPermissionSet(permissions) {
    let userPermissionSetObj = {};
    permissions.forEach(perm => {
      userPermissionSetObj[perm] = true;
    });
    return UserPermissionSet(userPermissionSetObj);
  }

  /**
   * Return the error of a field, if any, so that <orm /> can render it for the user to see
   * @param field form field who might have an error
   * @returns {*}
   */
  getError(field) {
    const errorMessage = this.props.editState.formState.getIn(['fieldErrors', field]);
    const fieldTouched = this.props.editState.formState.getIn(['fieldTouched', field]);
    const allFieldTouched = this.props.editState.formState.get('allTouched');

    if (fieldTouched || allFieldTouched) {
      return errorMessage;
    }
  }

  /**
   * Return whether the user's seat has been disabled by the admin or not currently
   * @return {boolean} userDisabled
   * @private
   */
  userSeatDisabled() {
    const userInfo = this.props.editState.formState.fieldValues.userInfo;
    return userInfo.hidden || !userInfo.active;
  }

  userActive() {
    const isNewUser = !this.props.editState.formState.fieldValues.userInfo.userId;
    const isActiveUser = this.props.editState.formState.fieldValues.userInfo.active;
    return isActiveUser || (isNewUser && !this.userSeatDisabled());
  }
}
